<script setup lang="ts">
import type { Icons } from '@/components/base/BaseIcon.vue'

const props = withDefaults(
  defineProps<{
    icon?: Icons
    iconColor?: string
    id?: number
    isDefaultOpen?: boolean
    hasBorder?: boolean
    subtitle?: string
    title?: string
    titlePaddings?: string
    titleBgColor?: string
    panelClass?: string
  }>(),
  {
    icon: null,
    iconColor: '',
    id: null,
    isDefaultOpen: false,
    hasBorder: true,
    subtitle: '',
    title: '',
    titlePaddings: '!px-2 !py-4 md:!px-8',
    titleBgColor: '',
    panelClass: ''
  }
)

const slots = useSlots()
const hasTitleSlot = !!slots['title']

const isOpen = ref(false)

const toggleDisclosure = (value: string | undefined) => {
  isOpen.value = value === '0'
}

onMounted(() => {
  isOpen.value = props.isDefaultOpen
})
</script>

<template>
  <Accordion type="single" :model-value="isOpen ? '0' : undefined" collapsible @update:model-value="toggleDisclosure">
    <AccordionItem
      value="0"
      :class="['rounded-lg overflow-visible bg-white', hasBorder ? '!border' : '!border-b-0 border-none', panelClass]"
    >
      <AccordionTrigger
        :class="[
          'cursor-pointer items-center justify-between !text-sm font-medium text-foreground focus:outline-hidden flex w-full hover:no-underline',
          isOpen && hasBorder ? '!border-b !border-border' : '!border-b-0',
          hasBorder && 'focus-visible:!ring-3 focus-visible:!ring-opacity-75',
          titlePaddings,
          titleBgColor
        ]"
      >
        <slot v-if="hasTitleSlot" name="title" />

        <div v-else class="flex flex-col">
          <div class="flex items-center gap-2">
            <span
              v-if="icon"
              class="text-md mr-2 rounded-full px-1 py-1"
              :style="`color: ${iconColor}; background-color: ${iconColor}4f;`"
            >
              <BaseIcon :name="icon" class="h-6 w-6" />
            </span>
            <h2 class="font-medium text-foreground">{{ title }}</h2>
          </div>
          <p v-if="subtitle" id="disclosureSubtitle" class="text-sm font-normal text-foreground-secondary">
            {{ subtitle }}
          </p>
        </div>
      </AccordionTrigger>

      <AccordionContent>
        <slot />
      </AccordionContent>
    </AccordionItem>
  </Accordion>
</template>
